import { ButtonFloatingSubmit, InputTextArea, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import useCaseMutation from "@/hooks/useCaseMutation";
import { isAxiosError } from "@/libraries/helper";
import {
	CreateRequestAccessCasePayloadSchema,
	DataIdSchema,
	ExistPatientRequestCaseSearchSchema,
} from "@/schemas";
import type { ResError } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import omit from "lodash/omit";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute(
	"/dashboard/case/create/exist/request/$caseId",
)({
	params: {
		parse: (params) => ({
			caseId: DataIdSchema.parse(Number(params.caseId)),
		}),
		stringify: ({ caseId }) => ({
			caseId: caseId.toString(),
		}),
	},
	validateSearch: zodSearchValidator(ExistPatientRequestCaseSearchSchema),
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const caseId = Route.useParams({
		select: (state) => state.caseId,
	});

	const caseName = Route.useSearch({
		select: (s: ExistPatientRequestCaseSearchSchema) => s.case_name ?? "case",
	});

	const searchPatientId = Route.useSearch({
		select: (s: ExistPatientRequestCaseSearchSchema) => s.pid,
	});
	const searchPatientDob = Route.useSearch({
		select: (s: ExistPatientRequestCaseSearchSchema) => s.pdob,
	});
	const searchPatientSex = Route.useSearch({
		select: (s: ExistPatientRequestCaseSearchSchema) => s.psex,
	});

	const {
		control,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<CreateRequestAccessCasePayloadSchema>({
		mode: "all",
		resolver: zodResolver(CreateRequestAccessCasePayloadSchema),
		defaultValues: {
			caseId,
			message: "",
		},
	});

	const { requestAccess } = useCaseMutation();

	const onSubmit: SubmitHandler<CreateRequestAccessCasePayloadSchema> = async (
		data,
		e,
	) => {
		e?.stopPropagation();
		e?.preventDefault();
		try {
			await requestAccess.mutateAsync(data);
			return navigate({
				to: "/dashboard/case/create/exist/request-complete",
				replace: true,
				search: searchPatientId
					? {
							step: 2,
							pid: searchPatientId,
							pdob: searchPatientDob,
							psex: searchPatientSex,
						}
					: undefined,
			});
		} catch (error) {
			if (isAxiosError(error, 422)) {
				const message = String(
					// @ts-expect-error
					error.response?.data?.messages ?? "Failed to request access",
				);
				return toast.error(message.replace("admin", `${caseName}`));
			}
			return toast.error(
				(error as ResError)?.response?.data?.messages ??
					"Failed to request access",
			);
		}
	};

	return (
		<>
			<BackAppBar>
				<div className="container pb-2">
					<Typography fontWeight={600} fontSize={28}>
						Request access
					</Typography>
				</div>
			</BackAppBar>
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="container h-full flex flex-col gap-8 bg-white"
			>
				<Typography fontWeight={400} fontSize={14}>
					Send a message to access this case info
				</Typography>
				<Controller
					control={control}
					name="message"
					defaultValue=""
					render={({ field, fieldState }) => (
						<InputTextArea
							fullWidth
							rows={4}
							placeholder="Message"
							aria-label="Request Message"
							error={!!fieldState.error?.message}
							helperText={fieldState.error?.message}
							{...omit(field, ["ref"])}
						/>
					)}
				/>
				<ButtonFloatingSubmit
					type="submit"
					label="Send"
					disabled={!isDirty || !isValid || isSubmitting}
				/>
			</form>
		</>
	);
}
